import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  type CreateProjectInput,
  type ProjectId,
  type Project,
  type UpdateProjectInput,
} from '@voll-intelligence/domain';
import { HttpError } from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { projectsRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { type AppIconName, useOrgContext, useSnackbar } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

export interface ProjectEditorValues {
  name: string;
  code: string;
  icon: AppIconName;
  description: string;
}

const EMPTY_VALUES: ProjectEditorValues = {
  name: '',
  code: '',
  icon: 'headset',
  description: '',
};

function projectToValues(project: Project): ProjectEditorValues {
  return {
    name: project.name,
    code: project.code,
    icon: project.icon as AppIconName,
    description: project.description ?? '',
  };
}

export function useProjectEditor() {
  const params = useParams({ strict: false });

  const projectId = params.projectId;
  const mode: 'create' | 'edit' = projectId ? 'edit' : 'create';

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { orgId, orgSlug } = useOrgContext();

  const { data: project, isLoading } = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId as ProjectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId as ProjectId),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateProjectInput) => projectsRepository.createProject(orgId, input),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: ProjectId; input: UpdateProjectInput }) =>
      projectsRepository.updateProject(orgId, id, input),
  });

  const initialValues = project ? projectToValues(project) : EMPTY_VALUES;
  const isReady = mode === 'create' ? true : !isLoading && !!project;

  async function createProject(value: ProjectEditorValues): Promise<void> {
    const created = await createMutation.mutateAsync({
      code: value.code as CreateProjectInput['code'],
      name: value.name,
      icon: value.icon,
      description: value.description || undefined,
    });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.cards.all }),
    ]);
    showSnackbar({ severity: 'success', message: t('project.editor.snackbar.created') });
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/project/$projectId',
        params: { orgSlug, projectId: created.id },
      }),
    );
  }

  async function updateProject(value: ProjectEditorValues): Promise<void> {
    await updateMutation.mutateAsync({
      id: projectId as ProjectId,
      input: {
        name: value.name,
        icon: value.icon,
        description: value.description || undefined,
      },
    });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.cards.all }),
    ]);
    showSnackbar({ severity: 'success', message: t('project.editor.snackbar.updated') });
    fireAndForget(navigate({ to: '/orgs/$orgSlug/overview', params: { orgSlug } }));
  }

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      try {
        if (mode === 'create') {
          await createProject(value);
        } else {
          await updateProject(value);
        }
      } catch (error) {
        // A 403 here means the caller's project perms changed (e.g.
        // demoted from project admin). Drop the cached project so
        // `useProjectAuthz` re-syncs and gates hide the save button.
        if (error instanceof HttpError && error.status === 403 && projectId) {
          fireAndForget(
            queryClient.invalidateQueries({
              queryKey: queryKeys.projects.detail(orgId, projectId as ProjectId),
            }),
          );
        }
        showSnackbar({
          severity: 'error',
          message: error instanceof Error ? error.message : t('project.editor.snackbar.error'),
        });
      }
    },
  });

  return {
    initialValues,
    mode,
    isReady,
    form,
    project,
  };
}
